import React, { useState, useCallback, useEffect } from "react";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import FileInput from "../../components/fileInput";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { Card, Toast, Form, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import {
  updatePartner,
  getPartnerDetails,
} from "./partnerSlice";
import { partnerUpdateSchema } from "../../schema/validationSchemas";

export default function Edit() {
  const { id: partnerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoader();
  const dispatch = useDispatch();

  const [partner, setPartner] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const params = new URLSearchParams(location.search);
  const currentPage = params.get("page") || 1;

  const fetchPartner = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(getPartnerDetails(partnerId));
      setPartner(result.payload.data);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching client:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, partnerId, setLoading]);

  useEffect(() => {
    setPartner(null);
    if (partnerId) {
      fetchPartner();
    }
  }, [partnerId, fetchPartner]);

  const handleCancel = () => {
    setPartner(null);
    navigate(`/clients?page=${currentPage}`);
  };

  const handleImageChange = (file, setFieldValue) => {
    if (file) {
      setNewImagePreview(URL.createObjectURL(file));
      setFieldValue("partner_image", file);
    }
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    const updatedValues = { ...values, id: partnerId };

    const formData = new FormData();
    formData.append("id", updatedValues.id);
    formData.append("_method", "PUT");
    formData.append("partner_title", updatedValues.partner_title);
    formData.append("partner_image", updatedValues.partner_image);

    await dispatch(updatePartner(formData))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem("partner_notify_message", res.payload.message);
          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          setPartner(null);
          navigate(`/clients?page=${currentPage}`);
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(
            res.payload.message || "Failed to update client!"
          );
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setSnackbarMessage("Failed to update client!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

  if (!partner)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <h2>Loading Client Details...</h2>
      </div>
    );

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      {partner && (
        <Formik
          initialValues={{
            partner_title: partner.partner_title || "",
            partner_image: "",
          }}
          validationSchema={partnerUpdateSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            values,
            setFieldValue,
          }) => (
            <Form
              onSubmit={handleSubmit}
              encType="multipart/form-data"
              className="bg-white p-4 rounded-2 shadow-sm"
            >
              <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
                <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
                <Row>
                  <Col md={12} className="mb-4">
                    <CommonFormLabel
                      htmlFor="partner_title"
                      title="Client Title"
                    />
                    <Input
                      id="partner_title"
                      name="partner_title"
                      type="text"
                      placeholder="Enter Client Title"
                      onChange={handleChange}
                      value={values.partner_title}
                      className={`shadow-none ${
                        errors?.partner_title && touched.partner_title
                          ? "error"
                          : "correct"
                      }`}
                    />
                    {touched.partner_title && errors?.partner_title && (
                      <ValidationFeedback title={errors?.partner_title} />
                    )}
                  </Col>
                </Row>
              </Card>

              <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
                <h4 className="mb-4 d-flex justify-content-center">Image</h4>
                <div className="mb-4">
                  <CommonFormLabel
                    htmlFor="partner_image"
                    title="Upload New Image"
                  />
                  <FileInput
                    name="partner_image"
                    onChange={(file) => handleImageChange(file, setFieldValue)}
                    error={errors?.partner_image}
                    touched={touched.partner_image}
                  />
                  {touched.partner_image && errors?.partner_image && (
                    <ValidationFeedback title={errors?.partner_image} />
                  )}
                </div>

                <Row>
                  {newImagePreview && (
                    <Col md={6} className="">
                      <CommonFormLabel
                        htmlFor="new_selected_image"
                        title="New selected Image"
                      />
                      <div>
                        <img
                          src={newImagePreview}
                          alt="New Selected"
                          className="img-fluid rounded border"
                          style={{ width: "200px", height: "auto" }}
                        />
                      </div>
                    </Col>
                  )}

                  <Col md={6} className="">
                    <CommonFormLabel
                      htmlFor="partner_image"
                      title="Uploaded Image"
                    />
                    <div className="mt-2">
                      <img
                        src={partner.partner_image}
                        alt="Client"
                        className="img-fluid rounded border"
                        style={{ width: "200px", height: "auto" }}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
              <hr className="my-5" />

              <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
                <CommonButton
                  title={isSubmitting ? "Updating..." : "Update"}
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary primary-shadow"
                />
                <CommonButton
                  title="Cancel"
                  onClick={handleCancel}
                  className="btn-muted"
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}
